
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import gameService from '../../../services/gameService';
import AlertModal from '../../common/AlertModal';
import { Button, Card, Spinner, InputGroup, FormControl, Row, Col, Modal, Alert } from 'react-bootstrap';
import { FaHandPaper, FaPlay, FaRegHandRock, FaHistory, FaInfoCircle, FaCoins, FaCrown, FaArrowLeft, FaRedoAlt, FaTimesCircle, FaExchangeAlt } from 'react-icons/fa';
import styles from '../BlackJack/BlackJack.module.css';

const QUICK = [1,5,10,25];

const BlackJackSwitch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [betAmount, setBetAmount] = useState(1);
  const [leftHand, setLeftHand]   = useState([]);
  const [rightHand, setRightHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [leftScore, setLeftScore] = useState(0);
  const [rightScore,setRightScore]= useState(0);
  const [dealerScore,setDealerScore]=useState(0);
  const [activeHand, setActiveHand]= useState(0);
  const [gameInProgress, setGameInProgress]=useState(false);
  const [gameOver, setGameOver]=useState(false);
  const [showResult, setShowResult]=useState(false);
  const [newBalance, setNewBalance]=useState(null);
  const [resumedMsg, setResumedMsg]=useState('');
  const [showConfirmAbort, setShowConfirmAbort]=useState(false);
  const [showErr, setShowErr]=useState(false);
  const [errText, setErrText]=useState('');
  const [loading, setLoading]=useState(false);

  const getImg = (c)=> `${process.env.PUBLIC_URL}/images/games/cards/${c}.png`;

  const handleError = (e) => {
    if (e?.code === 'GAME_UNAVAILABLE') {
      setErrText(t('game.unavailable') || 'Este juego no está disponible ahora.');
    } else {
      setErrText(e?.error || e?.message || t('blackjack.error_processing'));
    }
    setShowErr(true);
  };

  const start = async () => {
    setLoading(true); setResumedMsg('');
    try {
      const r = await gameService.playBlackJackSwitch(betAmount);
      if (r?.resumed) setResumedMsg(t('blackjack.resumed') || 'Ronda reanudada.');
      setLeftHand(r.leftHand||[]); setRightHand(r.rightHand||[]); setDealerHand(r.dealerHand||[]);
      setLeftScore(r.leftScore||0); setRightScore(r.rightScore||0); setDealerScore(r.dealerScore||0);
      setActiveHand(r.activeHand ?? 0);
      setNewBalance(r.newBalance ?? null);
      setGameInProgress(true); setGameOver(false); setShowResult(false);
    } catch (e) { handleError(e); setGameInProgress(false); }
    finally { setLoading(false); }
  };

  const doSwitch = async () => {
    if (!gameInProgress || gameOver) return;
    setLoading(true);
    try {
      const r = await gameService.switchBlackJackSwitch();
      setLeftHand(r.leftHand||[]); setRightHand(r.rightHand||[]);
      setLeftScore(r.leftScore||0); setRightScore(r.rightScore||0);
    } catch (e) { handleError(e); }
    finally { setLoading(false); }
  };

  const hit = async () => {
    if (!gameInProgress || gameOver) return;
    setLoading(true);
    try {
      const r = await gameService.hitBlackJackSwitch();
      setLeftHand(r.leftHand||[]); setRightHand(r.rightHand||[]); setDealerHand(r.dealerHand||[]);
      setLeftScore(r.leftScore||0); setRightScore(r.rightScore||0); setDealerScore(r.dealerScore||0);
      setActiveHand(r.activeHand ?? activeHand);
      if (r.gameOver) {
        setGameOver(true); setGameInProgress(false);
        setNewBalance(r.newBalance ?? null);
        setShowResult(true);
      }
    } catch (e) { handleError(e); }
    finally { setLoading(false); }
  };

  const stand = async () => {
    if (!gameInProgress || gameOver) return;
    setLoading(true);
    try {
      const r = await gameService.standBlackJackSwitch();
      setLeftHand(r.leftHand||[]); setRightHand(r.rightHand||[]); setDealerHand(r.dealerHand||[]);
      setLeftScore(r.leftScore||0); setRightScore(r.rightScore||0); setDealerScore(r.dealerScore||0);
      setActiveHand(r.activeHand ?? activeHand);
      if (r.gameOver) {
        setGameOver(true); setGameInProgress(false);
        setNewBalance(r.newBalance ?? null);
        setShowResult(true);
      }
    } catch (e) { handleError(e); }
    finally { setLoading(false); }
  };

  const abortRound = async () => {
    if (!gameInProgress || gameOver) return;
    setLoading(true);
    try {
      const out = await gameService.abortBlackJackSwitch(false);
      if (out?.aborted) resetUI();
      else { setErrText(t('blackjack.abort_failed') || 'No se pudo abandonar.'); setShowErr(true); }
    } catch (e) {
      const msg = e?.error || e?.message || '';
      if (String(msg).toLowerCase().includes('no open round')) { resetUI(); return; }
      handleError(e);
    } finally { setLoading(false); }
  };

  const resetUI = () => {
    setBetAmount(1); setLeftHand([]); setRightHand([]); setDealerHand([]);
    setLeftScore(0); setRightScore(0); setDealerScore(0);
    setActiveHand(0); setGameInProgress(false); setGameOver(false);
    setNewBalance(null); setShowResult(false); setResumedMsg('');
  };

  // resume al montar (silencioso si 204)
  useEffect(()=>{ (async ()=>{
    try {
      setLoading(true);
      const r = await gameService.resumeBlackJackSwitch?.();
      if (r) {
        setLeftHand(r.leftHand||[]); setRightHand(r.rightHand||[]); setDealerHand(r.dealerHand||[]);
        setLeftScore(r.leftScore||0); setRightScore(r.rightScore||0); setDealerScore(r.dealerScore||0);
        setActiveHand(r.activeHand ?? 0);
        setNewBalance(r.newBalance ?? null);
        setGameInProgress(true); setGameOver(false); setShowResult(false);
        setResumedMsg(t('blackjack.resumed') || 'Ronda reanudada.');
      }
    } catch(e){ handleError(e); }
    finally { setLoading(false); }
  })(); /* eslint-disable-next-line */ }, []);

  return (
    <div className={`${styles.container} d-flex justify-content-center align-items-center`}>
      <Card className={`${styles.table} text-center shadow-lg`}>
        <Card.Body>
          <div className="position-relative mb-4">
            <Button variant="secondary" onClick={()=>navigate('/sessions')} className={styles.backButton} title={t('blackjack.back')}>
              <FaArrowLeft />
            </Button>
            <h1 className={`${styles.title} text-center`}>
              <FaCrown className="me-2" /> {t('blackjack.switchTitle') || 'Blackjack Switch'} <FaCrown className="ms-2" />
            </h1>
          </div>

          {resumedMsg && <Alert variant="info" className="mt-2 py-2">{resumedMsg}</Alert>}

          {/* Chips + apuesta */}
          <div className={styles.chipsRow}>
            {QUICK.map((c)=>(
              <button key={c} className={`${styles.chip} ${betAmount===c?styles.chipActive:''}`}
                onClick={()=>setBetAmount(c)} disabled={gameInProgress||loading} title={`x${c}`}>
                <span className={styles.chipInner}>{c}</span>
              </button>
            ))}
            <div className={styles.chipsOps}>
              <button className={styles.chipOp} onClick={()=>setBetAmount((p)=>Math.max(1,p+1))} disabled={gameInProgress||loading}>+1</button>
              <button className={styles.chipOp} onClick={()=>setBetAmount((p)=>Math.max(1,p+5))} disabled={gameInProgress||loading}>+5</button>
              <button className={styles.chipOp} onClick={()=>setBetAmount(1)} disabled={gameInProgress||loading}>↺</button>
            </div>
          </div>

          <div className="mb-3">
            <InputGroup className={styles.betGroup}>
              <InputGroup.Text className={styles.betIcon}><FaCoins /></InputGroup.Text>
              <FormControl type="number" value={betAmount} onChange={(e)=>setBetAmount(Math.max(1, Number(e.target.value)))} min="1" disabled={gameInProgress||loading} className={styles.betInput}/>
              <InputGroup.Text className={styles.betUnit}>MENEL (x2)</InputGroup.Text>
            </InputGroup>
          </div>

          {/* Botones */}
          <div className={`${styles.actions} d-flex justify-content-around flex-wrap mb-3`}>
            <Button className={`${styles.blackjackButton} ${styles.startButton}`} onClick={start} disabled={loading||gameInProgress||betAmount<1}>
              {loading && !gameInProgress ? <Spinner size="sm" animation="border" /> : <> <FaPlay className="me-2" /> {t('blackjack.start')} </>}
            </Button>

            <Button className={`${styles.blackjackButton}`} style={{background: 'linear-gradient(180deg,#ffe39d 0%,#ffc04d 100%)'}}
              onClick={doSwitch} disabled={loading||!gameInProgress||gameOver} title={t('blackjack.switch') || 'Switch'}>
              <FaExchangeAlt className="me-2" /> {t('blackjack.switch') || 'Switch'}
            </Button>

            <Button className={`${styles.blackjackButton} ${styles.hitButton}`} onClick={hit} disabled={loading||!gameInProgress||gameOver}>
              {loading ? <Spinner size="sm" animation="border" /> : <> <FaHandPaper className="me-2" /> {t('blackjack.hit')}</>}
            </Button>

            <Button className={`${styles.blackjackButton} ${styles.standButton}`} onClick={stand} disabled={loading||!gameInProgress||gameOver}>
              {loading ? <Spinner size="sm" animation="border" /> : <> <FaRegHandRock className="me-2" /> {t('blackjack.stand')}</>}
            </Button>

            <Button className={`${styles.blackjackButton} ${styles.restartButton}`} onClick={gameInProgress && !gameOver ? ()=>setShowConfirmAbort(true) : resetUI} disabled={loading}>
              <FaRedoAlt className="me-2" /> {gameInProgress&&!gameOver ? (t('blackjack.abort')||'Abandonar') : t('blackjack.restart')}
            </Button>
          </div>

          {/* Mesa: dos manos del jugador */}
          <Row className="mt-4">
            {[{label:'Left', hand:leftHand, score:leftScore, idx:0},{label:'Right', hand:rightHand, score:rightScore, idx:1}].map(({label, hand, score, idx})=>(
              <Col key={idx}>
                <h4 className={styles.handTitle}>
                  {t('blackjack.player')} – {label}
                  {gameInProgress && !gameOver && activeHand===idx ? ' • ' + (t('blackjack.active')||'Activa') : ''}
                </h4>
                <div className={`${styles.cards} d-flex justify-content-center`}>
                  {hand.map((c,i)=>(
                    <img key={`${label}-${i}`} src={getImg(c)} alt={c} className={`${styles.cardImage} ${styles.animatedCard}`} style={{animationDelay:`${i*0.08}s`}}/>
                  ))}
                </div>
                <p className="mt-2">{t('blackjack.score')}: <strong>{score}</strong></p>
              </Col>
            ))}

            <Col xs={12} className="mt-4">
              <h4 className={styles.handTitle}>{t('blackjack.dealer')}</h4>
              <div className={`${styles.cards} d-flex justify-content-center`}>
                {dealerHand.map((c,i)=>(
                  <img key={`d-${i}`} src={getImg(c)} alt={c} className={`${styles.cardImage} ${styles.animatedCard}`} style={{animationDelay:`${i*0.08}s`}}/>
                ))}
              </div>
              <p className="mt-2">{t('blackjack.score')}: <strong>{gameOver? dealerScore : (dealerHand.length? (t('blackjack.hidden')||'Oculto') : 0)}</strong></p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Resultado */}
      <Modal show={showResult} onHide={()=>setShowResult(false)} centered>
        <Modal.Header closeButton className={styles.modalHeader}><Modal.Title>{t('blackjack.resultTitle')}</Modal.Title></Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className="text-center">
            <p className="mt-2"><strong>{t('blackjack.player')} – Left:</strong> {leftScore}</p>
            <p><strong>{t('blackjack.player')} – Right:</strong> {rightScore}</p>
            <p><strong>{t('blackjack.dealerScore')}:</strong> {dealerScore}</p>
            {newBalance !== null && <p className="mt-3"><strong>{t('blackjack.newBalance')}:</strong> {newBalance} MENEL</p>}
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="primary" onClick={()=>setShowResult(false)}><FaTimesCircle className="me-2" /> {t('blackjack.close')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmar Abandono */}
      <AlertModal
        show={showConfirmAbort}
        onHide={()=>setShowConfirmAbort(false)}
        title={t('blackjack.abort') || 'Abandonar ronda'}
        message={t('blackjack.abortConfirm') || '¿Abandonar la ronda? Esto no reembolsa la apuesta.'}
        actions={[
          { label: t('blackjack.cancel') || 'Cancelar', variant: 'secondary', onClick: ()=>setShowConfirmAbort(false) },
          { label: t('blackjack.confirm') || 'Confirmar', variant: 'danger', onClick: async ()=>{ setShowConfirmAbort(false); await abortRound(); } }
        ]}
      />

      {/* Error */}
      <AlertModal show={showErr} onHide={()=>setShowErr(false)} title={t('blackjack.errorTitle')||'Error'} message={errText}/>
    </div>
  );
};

export default BlackJackSwitch;
